import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Sparkles, Trophy, Lock, Users, UserPlus, MessageSquare, Calendar,
  Building2, Handshake, Microscope, Lightbulb,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const METRIC_CONFIG = [
  { key: 'members',             label: 'Members',            icon: Users,          color: 'text-amber-600' },
  { key: 'new_members_week',    label: 'New this week',      icon: UserPlus,       color: 'text-emerald-600' },
  { key: 'posts_week',          label: 'Posts this week',    icon: MessageSquare,  color: 'text-blue-600' },
  { key: 'events_month',        label: 'Events this month',  icon: Calendar,       color: 'text-purple-600' },
  { key: 'clubs_active',        label: 'Active clubs',       icon: Building2,      color: 'text-orange-600' },
  { key: 'partners',            label: 'Partners',           icon: Handshake,      color: 'text-rose-600' },
  { key: 'research_articles',   label: 'Research articles',  icon: Microscope,     color: 'text-indigo-600' },
  { key: 'suggestions_pending', label: 'Pending suggestions',icon: Lightbulb,      color: 'text-yellow-600' },
];

export default function CommunityPulse() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/community/pulse`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="container mx-auto px-4 py-16 text-center text-muted-foreground">Loading community pulse...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-10 max-w-5xl">
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-heading font-bold flex items-center gap-3" data-testid="community-pulse-title">
          <Sparkles className="w-9 h-9 text-amber-500" />
          Community Pulse
        </h1>
        <p className="text-base text-muted-foreground mt-2 max-w-2xl">
          Watch AADC grow in real time. Every member, post, and event moves us closer
          to unlocking new AI-powered features — built for the community, by the community.
        </p>
      </div>

      {/* Metrics grid */}
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {METRIC_CONFIG.map((m) => {
          const Icon = m.icon;
          const value = data.metrics?.[m.key] ?? 0;
          return (
            <Card key={m.key} data-testid={`page-metric-${m.key}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-5 h-5 ${m.color}`} />
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">{m.label}</span>
                </div>
                <div className="text-3xl font-semibold tabular-nums">{value.toLocaleString()}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Unlock roadmap */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            Feature Unlock Roadmap
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            New AI capabilities unlock automatically as the community grows. Help us get there — invite friends,
            create posts, host events.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          {data.unlocks?.map((u) => {
            const pct = Math.min(100, Math.round((u.current / u.threshold) * 100));
            const state = u.enabled ? 'live' : u.unlocked ? 'unlocked' : 'locked';
            const Icon = u.enabled ? Sparkles : u.unlocked ? Trophy : Lock;
            const barColor = u.enabled ? 'bg-emerald-500' : u.unlocked ? 'bg-amber-500' : 'bg-primary/60';
            const iconColor = u.enabled ? 'text-emerald-500' : u.unlocked ? 'text-amber-500' : 'text-muted-foreground';
            const badgeText = state === 'live' ? 'Live now' : state === 'unlocked' ? 'Unlocked — enabling soon' : 'Locked';
            const badgeClass =
              state === 'live'
                ? 'bg-emerald-100 text-emerald-800'
                : state === 'unlocked'
                ? 'bg-amber-100 text-amber-800'
                : 'bg-muted text-muted-foreground';
            return (
              <div key={u.id} data-testid={`page-unlock-${u.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${iconColor}`} />
                    <div>
                      <div className="font-medium">{u.label}</div>
                      {u.description && (
                        <p className="text-xs text-muted-foreground">{u.description}</p>
                      )}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${badgeClass}`}>{badgeText}</span>
                </div>
                <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
                  <div className={`h-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="flex items-center justify-between mt-1 text-xs text-muted-foreground">
                  <span>{pct}% there</span>
                  <span className="tabular-nums">{u.current.toLocaleString()} / {u.threshold.toLocaleString()} members</span>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground mt-6 text-center">
        Updated every 10 minutes · last refresh {new Date(data.generated_at).toLocaleTimeString()}
      </p>
    </div>
  );
}
